/* eslint-disable no-undef-init */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import Map, {
	Layer,
	LineLayer,
	MapRef,
	Marker,
	NavigationControl,
	Source,
} from 'react-map-gl';
import mapboxgl from 'utils/mapboxgl';
// @ts-ignore	eslint-disable-next-line no-undef
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
	Button,
	Input,
	ModalConfirmation,
	Typography,
} from 'design_system/src';
import { LngLatBounds } from 'mapbox-gl';

import { InterestPointTypeValues } from 'models/repossession/enums/repossession.enums';
import { momentLocal } from 'models/shared/helpers/dates';
import { ReactComponent as Menu } from 'design_system/src/static/icons/drag-indicator.svg';
import { ReactComponent as Workflow } from 'design_system/src/static/icons/workflow.svg';
import { ReactComponent as Moto } from 'design_system/src/static/icons/motorcycle.svg';

import {
	AdditionRecoveriesDto,
	CreateRecoveriesDto,
} from 'models/repossession/interfaces/recovery.interface';
import type { FeatureCollection } from 'geojson';
import {
	additionToRoute,
	createRoute,
	FetchRoutes,
} from 'models/repossession/controllers/repossession.controller';

import { LightTooltip } from 'components/hocs/tooltip/Tooltip';
import '../../recovery.scss';
import { toasterNotification } from 'models/shared/helpers/toasterNotification';
import { toasterOptions } from 'models/shared/enums/toaster.enum';
import { commonErrorHandler } from 'models/shared/helpers/errors';
import { clearRepossessionRoute } from 'localStorage/repossessionStorage';
import {
	activeRouteStatuses,
	RouteStatusValues,
} from 'models/repossession/enums/route.enums';
import moment from 'moment';
import { Route } from 'models/repossession/interfaces/route.interface';
import { PaginatedDocuments } from 'models/shared/types/pagination';
import { Grid } from '@mui/material';
import { repossessionContext } from '../../RepossessionsContext';
import {
	CreateRouteSteps,
	PlainLocation,
	RepossessionTabs,
} from '../../Repossessions.constants';

export const CreateRouteMap = () => {
	const mapRef = useRef<MapRef>(null);

	const {
		setCreateRouteStep,
		selectedInterestPoints,
		clearContext,
		ozonios,
		setViewTab,
	} = useContext(repossessionContext);

	const [viewState, setViewState] = useState({
		longitude: -99.12766,
		latitude: 19.42847,
		zoom: 10,
	});
	const [routeInterestPoints, setRouteInterestPoints] = useState<
		PlainLocation[]
	>([]);
	const [loadingRoute, setLoadingRoute] = useState(false);

	const [routeDate, setRouteDate] = useState<string>(
		momentLocal().format('YYYY-MM-DD'),
	);
	const [team, setTeam] = useState([]);

	const [openRouteSelect, setOpenRouteSelect] = useState(false);
	const [routes, setRoutes] = useState<Route[]>([]);
	const [selectedRoute, setSelectedRoute] = useState<Route>();

	const clearRouteSelect = () => {
		setOpenRouteSelect(false);
		setSelectedRoute(undefined);
	};

	const routeLayer = useMemo(() => {
		const source: FeatureCollection = {
			type: 'FeatureCollection',
			features: [
				{
					type: 'Feature',
					properties: {},
					geometry: {
						type: 'LineString',
						coordinates: routeInterestPoints.map((point) => [
							point.coordinates.long,
							point.coordinates.lat,
						]),
					},
				},
			],
		};
		const layerStyle: LineLayer = {
			id: 'route',
			type: 'line',
			paint: {
				'line-color': '#888',
				'line-width': 3,
			},
		};
		return source?.features?.length ? (
			<Source type="geojson" data={source}>
				<Layer {...layerStyle} />
			</Source>
		) : null;
	}, [routeInterestPoints]);

	useEffect(() => {
		FetchRoutes(1, activeRouteStatuses, undefined, undefined).then(
			(resp: PaginatedDocuments<Route>) => {
				setRoutes(resp.data);
			},
		);
	}, []);

	useEffect(() => {
		const flatInterestPoints = Object.values(selectedInterestPoints).flatMap(
			(points) => points,
		);
		setRouteInterestPoints(flatInterestPoints);
		centerRoute(flatInterestPoints);
	}, [selectedInterestPoints]);

	const markers = useMemo(() => {
		return routeInterestPoints.map((point, i) => {
			const Icon = InterestPointTypeValues[point.locationType].icon;
			return (
				<Marker
					key={point.id}
					longitude={point.coordinates.long}
					latitude={point.coordinates.lat}
				>
					<div
						className={`br_xxl main_marker cursor_pointer ${
							InterestPointTypeValues[point.locationType].color
						}`}
					>
						<div className="marker_info p_y_xs p_x_lg flex_center flex_gap_sm">
							<div
								className={`dim_lg pos_relative br_circle bg_${
									InterestPointTypeValues[point.locationType].realColor
								}_nocontrast`}
							>
								<Typography
									weight="400"
									scale="xsmall"
									textColor="neutral_0"
									className="pos_absolute center"
								>
									{i + 1}
								</Typography>
							</div>
							<Typography weight="600" scale="small">
								{point.vehicleId}
							</Typography>
						</div>
						<div className="marker_details p_y_xxs p_x_md flex_center z_index_1 pos_relative">
							<Icon className="dim_lg icon" />
							<div className="p_x_md flex_center_col">
								<Typography weight="600" scale="xsmall">
									{momentLocal(point.createdAt).format('DD/MM/YYYY')}
								</Typography>
								<Typography weight="400" scale="xsmall">
									{momentLocal(point.createdAt).format('HH:mm')}
								</Typography>
							</div>
						</div>
						<span className="arrow" />
					</div>
				</Marker>
			);
		});
	}, [viewState.zoom, routeInterestPoints]);

	const triggerCreateRoute = () => {
		if (!team || !routeDate) return;
		setLoadingRoute(true);
		const payload: CreateRecoveriesDto = {
			route: {
				routeDate,
				team,
			},
			recoveries: routeInterestPoints.map((point, index) => {
				let interestPointData = {};
				if (point.interestPointId) {
					interestPointData = { interestPointId: point.interestPointId };
				} else {
					interestPointData = {
						interestPointData: {
							type: point.locationType,
							loan: point.loanId,
							coordinates: point.coordinates,
							eventDate: point.createdAt,
							externalId: point.externalId,
						},
					};
				}
				return {
					...interestPointData,
					index,
					repossession: point.repossessionId,
				};
			}),
		};

		createRoute(payload)
			.then(() => {
				toasterNotification({
					msg: 'Se creo la ruta',
					toasterType: toasterOptions.success,
					style: 'light',
				});
				clearContext();
				clearRepossessionRoute();
				setViewTab(RepossessionTabs.routes);
				setLoadingRoute(false);
			})
			.catch((e) => {
				setLoadingRoute(false);
				commonErrorHandler(e);
			});
	};

	const triggerAddToRoute = () => {
		if (!selectedRoute) return;
		setLoadingRoute(true);
		const payload: AdditionRecoveriesDto = {
			routeId: selectedRoute._id,
			recoveries: routeInterestPoints.map((point, index) => {
				let interestPointData = {};
				if (point.interestPointId) {
					interestPointData = { interestPointId: point.interestPointId };
				} else {
					interestPointData = {
						interestPointData: {
							type: point.locationType,
							loan: point.loanId,
							coordinates: point.coordinates,
							eventDate: point.createdAt,
							externalId: point.externalId,
						},
					};
				}
				return {
					...interestPointData,
					index,
					repossession: point.repossessionId,
				};
			}),
		};

		additionToRoute(payload)
			.then(() => {
				toasterNotification({
					msg: 'Se edito la ruta',
					toasterType: toasterOptions.success,
					style: 'light',
				});
				clearContext();
				clearRepossessionRoute();
				setViewTab(RepossessionTabs.routes);
				setLoadingRoute(false);
			})
			.catch((e) => {
				setLoadingRoute(false);
				commonErrorHandler(e);
			});
	};

	const onDragEnd = (result: any) => {
		// dropped outside the list
		if (!result.destination || !routeInterestPoints) {
			return;
		}

		const reorder = Array.from(routeInterestPoints);
		const [removed] = reorder.splice(result.source.index, 1);
		reorder.splice(result.destination.index, 0, removed);

		setRouteInterestPoints(reorder);
	};

	const centerRoute = (points: PlainLocation[]) => {
		if (points?.length) {
			const bounds = new LngLatBounds();
			points.forEach((point) => {
				bounds.extend([point.coordinates.long, point.coordinates.lat]);
			});
			const size = (bounds.getEast() - bounds.getWest()) * 2.2;
			bounds.extend({
				lng: bounds.getWest() - size,
				lat: bounds.getNorth(),
			});
			mapRef.current?.fitBounds(bounds, { padding: 100 });
		}
	};

	return selectedInterestPoints ? (
		<div className="dim_100_per">
			<div className="repossession_map_route_cont h_100_per w_40_per display_flex flex_col flex_gap_md">
				<div className="flex_content_col route_cont overflow_hidden br_xs shadow_hard bg_neutral_0 flex_grow_1">
					<div className="flex_header p_md p_b_none">
						<div className=" display_flex flex_gap_md ">
							<Input
								type="short-date"
								name="fecha"
								title="Fecha de ruta"
								className="flex_basis_0 flex_grow_1"
								value={routeDate}
								onChange={(e: any) => setRouteDate(e.target.value)}
							/>
							<Input
								type="select"
								name="ozonio"
								title="Encargado"
								placeholder="Encargado"
								className="flex_basis_0 flex_grow_1"
								multiple
								options={ozonios}
								value={team}
								onChange={(e: any) => setTeam(e.target.value)}
							/>
						</div>
						<div className="display_flex flex_gap_md p_b_sm">
							<Typography
								scale="medium"
								weight="600"
								className="route_fixed_components text_center"
							>
								Orden
							</Typography>
							<Typography
								scale="medium"
								weight="600"
								className="route_fixed_components text_center"
							>
								Tipo
							</Typography>
							<Typography
								scale="medium"
								weight="600"
								className="flex_1 text_center"
							>
								Vehículo
							</Typography>
							<div className="route_fixed_components" />
						</div>
					</div>
					<div className="flex_body p_md p_t_none display_flex flex_col ">
						<DragDropContext onDragEnd={onDragEnd}>
							<Droppable droppableId="droppable">
								{(provided: any, snapshot: any) => (
									<div
										{...provided.droppableProps}
										ref={provided.innerRef}
										className="flex_grow_1 bg_neutral_300 display_flex flex_col flex_gap_sm p_xxs"
									>
										{routeInterestPoints?.map((point, index) => {
											const icon = InterestPointTypeValues[point.locationType];

											return (
												<Draggable
													key={point.id}
													draggableId={point.id}
													index={index}
												>
													{(provided2: any, snapshot2: any) => (
														<div
															className={`route_component  ${
																snapshot2.isDragging
																	? 'bg_primary_50'
																	: 'bg_neutral_0'
															}`}
															ref={provided2.innerRef}
															{...provided2.draggableProps}
															{...provided2.dragHandleProps}
															style={provided2.draggableProps.style}
														>
															<div className="display_flex flex_align_center flex_gap_md flex_justify_between p_y_sm">
																<Typography
																	scale="medium"
																	weight="400"
																	className="route_fixed_components text_center"
																>
																	{index + 1}
																</Typography>
																<LightTooltip title={icon.title}>
																	<div className="route_fixed_components flex_center">
																		<icon.icon
																			className={`dim_lg icon text_${icon.realColor}`}
																		/>
																	</div>
																</LightTooltip>
																<Typography
																	scale="medium"
																	weight="400"
																	className="flex_1 text_center"
																>
																	{point.vehicleId}
																</Typography>
																<div className="route_fixed_components flex_center">
																	<Menu className="dim_lg" />
																</div>
															</div>
														</div>
													)}
												</Draggable>
											);
										})}
										{provided.placeholder}
									</div>
								)}
							</Droppable>
						</DragDropContext>
					</div>
				</div>
				<Button
					className="create_route_prev_button"
					color="grey"
					onClick={() =>
						setCreateRouteStep(CreateRouteSteps.selectInterestPoints)
					}
				>
					Anterior
				</Button>
			</div>

			<div className="repossession_map_next_button_cont display_flex flex_gap_md">
				<Button
					className="flex_basis_0 flex_grow_1"
					color="secondary"
					onClick={() => setOpenRouteSelect(true)}
					disabled={routeInterestPoints.length < 1 || loadingRoute}
				>
					Añadir a ruta existente
				</Button>
				<Button
					className="flex_basis_0 flex_grow_1"
					onClick={triggerCreateRoute}
					disabled={
						routeInterestPoints.length < 1 ||
						!routeDate ||
						routeDate === '' ||
						!team?.length ||
						loadingRoute
					}
				>
					Crear Ruta
				</Button>
			</div>
			<Map
				mapLib={mapboxgl}
				mapboxAccessToken={process.env.REACT_APP_MAPBOX_API_KEY || ''}
				onMove={(evt) => setViewState(evt.viewState)}
				mapStyle="mapbox://styles/mapbox/streets-v9"
				reuseMaps
				ref={mapRef}
				{...viewState}
			>
				<NavigationControl
					position="bottom-right"
					style={{ marginBottom: '6rem' }}
				/>
				{routeLayer}
				{markers}
			</Map>
			<ModalConfirmation
				openConfirmation={openRouteSelect}
				handleClose={() => clearRouteSelect()}
				icon={<Workflow className="text_red_300" />}
				widthDesktop={60}
				heightDesktop={80}
				isConfirmationLoading={false}
				actions={[
					{
						label: 'Volver',
						color: 'grey',
						action: () => clearRouteSelect(),
					},
					{
						label: 'Agregar',
						color: 'primary',
						action: () => triggerAddToRoute(),
						disabled: !selectedRoute || loadingRoute,
					},
				]}
			>
				<>
					<Typography scale="large" weight="600" className="m_b_xs">
						Agregar a ruta
					</Typography>
					<Typography
						scale="medium"
						weight="400"
						className="m_b_md"
						textColor="neutral_700"
					>
						Selecciona una ruta a la cual agregar las recuperacíones
					</Typography>
					<Grid container direction="row" spacing={3} className="">
						{routes?.map((route, i) => (
							<Grid item xs={6} key={route._id}>
								<div
									className={`br_sm cursor_pointer ${
										selectedRoute?._id === route._id
											? 'bg_primary_100'
											: 'bg_neutral_0'
									}`}
									tabIndex={0}
									role="button"
									onClick={() => {
										if (selectedRoute?._id === route._id) {
											setSelectedRoute(undefined);
										} else {
											setSelectedRoute(route);
										}
									}}
									onKeyDown={() => {
										if (selectedRoute?._id === route._id) {
											setSelectedRoute(undefined);
										} else {
											setSelectedRoute(route);
										}
									}}
								>
									<div className="display_flex flex_justify_between p_md p_b_xs flex_align_center">
										<div>
											<Typography
												weight="600"
												scale="medium"
												textColor="neutral_1000"
												className=""
											>
												{moment
													.utc(route.routeDate)
													.locale('es')
													.format('dddd, D MMMM')}
											</Typography>
											<Typography
												weight="400"
												scale="small"
												textColor="neutral_600"
												className=""
											>
												{route.team.map((ozonio) => ozonio.name).join(', ')}
											</Typography>
										</div>
									</div>
									<div className="p_md p_y_xs border_b_solid border_neutral_300 border_1">
										<div className="flex_center">
											<Typography
												weight="600"
												scale="small"
												textColor={
													RouteStatusValues[route.status].textColor as any
												}
												className={`bg_${
													RouteStatusValues[route.status].color
												}_nocontrast br_xl p_y_xxs p_x_sm m_y_xs`}
											>
												{RouteStatusValues[route.status].title}
											</Typography>
										</div>
										<div className="display_flex flex_gap_xs flex_align_center">
											<Moto className="dim_md" />
											<Typography
												weight="400"
												scale="medium"
												textColor="neutral_1000"
												className=""
											>
												{route.recoveries.length} puntos de interes a visitar
											</Typography>
										</div>
									</div>
								</div>
							</Grid>
						))}
					</Grid>
				</>
			</ModalConfirmation>
		</div>
	) : null;
};
